// Markdown textarea with a small formatting toolbar and a Write / Preview
// switch, used for video transcripts.

import { useRef, useState } from "react";
import { MarkdownPreview } from "./MarkdownPreview";

type Tool = { label: string; title: string; apply: () => void } | "sep";

export function MarkdownEditor({
  value,
  onChange,
  placeholder = "Transcript in Markdown. Headings, lists, bold and quotes render in the app.",
  minHeight = 220,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: number;
}) {
  const [preview, setPreview] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  function setCursor(pos: number) {
    setTimeout(() => {
      const ta = textareaRef.current;
      if (!ta) return;
      ta.focus();
      ta.selectionStart = ta.selectionEnd = pos;
    }, 0);
  }

  // Wraps the selection (or the cursor) in `before` … `after`.
  function wrap(before: string, after = "") {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = value.substring(start, end);
    onChange(value.substring(0, start) + before + selected + after + value.substring(end));
    setCursor(start + before.length + selected.length);
  }

  // Prefixes the current line (heading, list item, quote).
  function prefixLine(prefix: string) {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const lineStart = value.lastIndexOf("\n", start - 1) + 1;
    onChange(value.substring(0, lineStart) + prefix + value.substring(lineStart));
    setCursor(start + prefix.length);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key !== "Tab") return;
    e.preventDefault();
    const start = e.currentTarget.selectionStart;
    const end = e.currentTarget.selectionEnd;
    onChange(value.substring(0, start) + "  " + value.substring(end));
    setCursor(start + 2);
  }

  const tools: Tool[] = [
    { label: "B", title: "Bold", apply: () => wrap("**", "**") },
    { label: "I", title: "Italic", apply: () => wrap("*", "*") },
    { label: "S", title: "Strikethrough", apply: () => wrap("~~", "~~") },
    "sep",
    { label: "H", title: "Heading", apply: () => prefixLine("## ") },
    { label: "🔗", title: "Link", apply: () => wrap("[", "](url)") },
    { label: "</>", title: "Inline code", apply: () => wrap("`", "`") },
    { label: "{ }", title: "Code block", apply: () => wrap("\n```\n", "\n```\n") },
    "sep",
    { label: "•", title: "Bullet list", apply: () => prefixLine("- ") },
    { label: "1.", title: "Numbered list", apply: () => prefixLine("1. ") },
    { label: "“", title: "Quote", apply: () => prefixLine("> ") },
  ];

  return (
    <div className="co-md">
      <div className="co-md-bar">
        {tools.map((t, i) =>
          t === "sep" ? (
            <span key={`sep-${i}`} className="co-md-sep" />
          ) : (
            <button
              key={t.title}
              type="button"
              className="co-btn-icon"
              title={t.title}
              aria-label={t.title}
              disabled={preview}
              onClick={t.apply}
            >
              {t.label === "B" ? <strong>B</strong> : t.label === "I" ? <em>I</em> : t.label === "S" ? <s>S</s> : t.label}
            </button>
          ),
        )}
        <div className="seg co-md-mode">
          <button type="button" className={preview ? "" : "active"} onClick={() => setPreview(false)}>
            Write
          </button>
          <button type="button" className={preview ? "active" : ""} onClick={() => setPreview(true)}>
            Preview
          </button>
        </div>
      </div>
      {preview ? (
        <div className="co-md-preview" style={{ minHeight }}>
          <MarkdownPreview content={value} />
        </div>
      ) : (
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          style={{ minHeight }}
          aria-label="Markdown"
        />
      )}
      <div className="co-md-foot">
        <span>Supports Markdown · Tab for indent</span>
        <span>{value.length} characters</span>
      </div>
    </div>
  );
}
